// Padrao global de navegacao por teclado em dropdowns/autocompletes:
//   - ↓ / ↑ destaca proximo / anterior item da lista
//   - Enter seleciona o item destacado
//   - Item destacado entra no campo de visao (scrollIntoView automatico)
//
// Uso tipico em qualquer autocomplete inline (input + lista de resultados):
//
//   const teclado = useListaTeclado(resultados, (item) => selecionar(item));
//   <input onKeyDown={teclado.onKeyDown} ... />
//   <div ref={teclado.listaRef}>
//     {resultados.map((r, idx) => (
//       <div data-idx={idx} className={idx === teclado.highlight ? 'destaque' : ''}
//            onMouseEnter={() => teclado.setHighlight(idx)} ...>
//         ...
//       </div>
//     ))}
//   </div>
//
// O componente <ComboBox/> ja usa esse mesmo padrao internamente.

import { useEffect, useRef, useState } from 'react';

export function useListaTeclado<T>(items: T[], onSelecionar: (item: T) => void) {
  const [highlight, setHighlight] = useState(0);
  const listaRef = useRef<HTMLDivElement | null>(null);

  // Reseta destaque pro topo sempre que a lista muda (busca nova, filtro mudou).
  useEffect(() => { setHighlight(0); }, [items]);

  // Garante que o item destacado esteja visivel.
  useEffect(() => {
    if (!listaRef.current) return;
    const el = listaRef.current.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      const item = items[highlight];
      if (item) {
        e.preventDefault();
        onSelecionar(item);
      }
    }
  }

  return { highlight, setHighlight, listaRef, onKeyDown };
}
